import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { UserMeResponse } from '../types/UserTypes';

export interface CurrentUser {
  Id: string;
  // The analyst's own (top-level) companies, i.e. BforeAI. Used to tell a
  // customer-authored comment from an analyst one (WHEEL-406).
  companyIds: number[];
}

export interface UserRole {
  Id: string;
  Name: string;
  [key: string]: unknown;
}

/**
 * The internal user id comes from management-service `GET /v2/user/me`
 * (WHEEL-405). It replaced dashboardapi `user/token`, which minted and stored
 * a throwaway Jarvis token on every call just to return the same id.
 * `tree=true` asks for the tree shape (ENG-902), so Wheel is not left on the
 * deprecated flat format that ENG-1015 removes. `user_id` is in both shapes;
 * the top-level `companies` are the analyst's own companies (children are
 * nested, so they are not picked up here).
 */
export const getCurrentUserApi = async (): Promise<CurrentUser> => {
  const response = await apiClient({
    method: 'GET',
    url: 'v2/user/me?tree=true',
  });
  const data: UserMeResponse | undefined = response?.data;
  // Fail loudly: an empty id would be written as user_id on threat updates.
  if (!data?.user_id) throw new Error('GET v2/user/me returned no user_id');
  return {
    Id: data.user_id,
    companyIds: (data.companies ?? []).map(company => company.company_id),
  };
};

export const getUserRoleApi = async () => {
  const config = { method: 'GET', url: 'role/all' };
  return apiClient(config);
};

export const currentUserQueryKey = ['user', 'current'];

/**
 * The analyst's internal user record (WHEEL-341f). Replaces the persisted
 * user slice: since WHEEL-342 every surface fetches this itself (deduped by
 * the shared cache), so persistence is no longer needed for deep links -
 * the record loads fresh on every session. Exposes `Id` and the analyst's
 * own `companyIds`.
 */
export const useCurrentUserQuery = (enabled = true) =>
  useQuery<CurrentUser>({
    queryKey: currentUserQueryKey,
    queryFn: getCurrentUserApi,
    staleTime: 5 * 60_000,
    enabled,
  });

export const userRolesQueryKey = ['user', 'roles'];

/** Role catalogue for the user-management modal. */
export const useUserRolesQuery = (enabled = true) =>
  useQuery<UserRole[]>({
    queryKey: userRolesQueryKey,
    queryFn: async () => {
      const response = await getUserRoleApi();
      return response?.data ?? [];
    },
    staleTime: 5 * 60_000,
    enabled,
  });
